import { useEffect } from "react";
import eventBus from "@sd-utility/eventbus";
import { captureAPMError } from "client/components/utils/captureError";

// Import UVBasketExist properly or mock it
import { UVBasketExist } from "./helpers";

export interface OdmDataType {
  s4l: boolean;
  basketCount: string;
}

interface UVBasketItem {
  quantity: number;
  product?: {
    warranty_price?: number;
  };
}

export let ENABLE_LEGACY_BASKET = true;

export const setEnableLegacyBasket = (value: boolean) => {
  ENABLE_LEGACY_BASKET = value;
};

export const fetchS4LCount = async (): Promise<{ s4lCount: boolean }> => {
  if (window.ODM?.s4l?.getList) {
    const { data } = await window.ODM.s4l.getList();

    return { s4lCount: data?.items.length > 0 };
  } else {
    console.error("ODM.s4l.getList is not available");
  }
  return { s4lCount: false };
};

export const fetchBasketCount = async (): Promise<{ basketCount: string }> => {
  if (!ENABLE_LEGACY_BASKET) {
    if (window.ODM?.basket?.getCount) {
      const response = await window.ODM.basket.getCount();
      if (response.status === 200) {
        return { basketCount: String(response.data ?? 0) };
      } else {
        console.error(`Unexpected status: ${response.status}`);
      }
    } else {
      console.error("ODM.basket.getCount is not available");
    }
    return { basketCount: "0" };
  } else {
    let uvItems: UVBasketItem[] = [];
    let basketSize = 0;

    if (window.universal_variable?.basket?.line_items) {
      uvItems = window.universal_variable.basket.line_items;
    }

    if (window.tvgThinDataLayer?.basketCount) {
      basketSize = window.tvgThinDataLayer.basketCount;
    } else if (UVBasketExist()) {
      // use the imported helper
      uvItems.forEach((item) => {
        const qty = item.quantity;
        basketSize += item.product?.warranty_price ? qty * 2 : qty;
      });
    }

    return { basketCount: String(basketSize) };
  }
};

export const useODMEffect = (
  setOdmData: React.Dispatch<React.SetStateAction<OdmDataType>>,
) => {
  useEffect(() => {
    (async () => {
      try {
        const { s4lCount } = await fetchS4LCount();
        const { basketCount } = await fetchBasketCount();
        setOdmData({ s4l: s4lCount, basketCount });
      } catch (err) {
        captureAPMError(err);
      }
    })();
  }, [setOdmData]);
};

export const useODMSubEffect = (
  setOdmData: React.Dispatch<React.SetStateAction<OdmDataType>>,
  odmData: OdmDataType,
) => {
  useEffect(() => {
    const basketSub = eventBus.subscribe("cart/count", (count: number) => {
      setOdmData((prev) => ({ ...prev, basketCount: String(count) }));
    });

    const s4lSub = eventBus.subscribe("s4l/hasItems", (value: unknown) => {
      setOdmData((prev) => ({ ...prev, s4l: value as boolean }));
    });

    const basketSubPrd = eventBus.subscribe("basket/add", () => {
      setOdmData((prev) => ({
        ...prev,
        basketCount: (Number(prev.basketCount) + 1).toString(),
      }));
    });

    return () => {
      basketSub.unsubscribe();
      s4lSub.unsubscribe();
      basketSubPrd.unsubscribe();
    };
  }, [setOdmData, odmData.s4l, odmData.basketCount]);
};
